import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, Input, OnInit, ViewChildren } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FloorplanUploadOverlayComponent } from "@components/floorplan-upload-overlay/floorplan-upload-overlay.component";
import { UploadedFloorplan } from "@components/floorplan-upload-overlay/uploaded-floorplan";
import { MobileFloorplansWarningComponent } from "@components/mobile-floorplans-warning/mobile-floorplans-warning.component";
import { NewProjectFileCardComponent } from "@components/project-cards/new-project-file-card/new-project-file-card.component";
import { ProjectDetailFileCardComponent } from "@components/project-cards/project-detail-file-card/project-detail-file-card.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { ProjectFloorplanData, ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { ModalService } from "@odx/angular/components/modal";
import { map, switchMap, tap } from "rxjs";
import { Breakpoints } from "../../../../styles/breakpoints";

@Component({
  selector: "app-floorplan-upload",
  templateUrl: "./floorplan-upload.component.html",
  styleUrls: ["./floorplan-upload.component.scss"],
  imports: [UiKitModule, FloorplanUploadOverlayComponent, ProjectDetailFileCardComponent, MobileFloorplansWarningComponent],
  standalone: true,
})
export class FloorplanUploadComponent implements OnInit {
  private router = inject(Router);

  private readonly modalService = inject(ModalService);
  private breakpointObserver = inject(BreakpointObserver);
  private projectFloorplanService = inject(ProjectFloorplanService);

  @ViewChildren("floorplanCard")
  floorplanCards!: NewProjectFileCardComponent[];

  @Input() set projectData(project: ScopedProject) {
    this.project = project;
  }

  project!: ScopedProject;
  floorplans: ProjectFloorplanData[] = [];
  isTablet$ = this.breakpointObserver.observe(Breakpoints.tablet).pipe(map((result) => result.matches));

  ngOnInit(): void {
    if (this.project.id) {
      this.loadFloorplans().subscribe();
    }
  }

  onDelete(floorplan: Floorplan) {
    this.modalService
      .open(ConfirmModalComponent, {
        data: {
          text: $localize`:@@modal.deleteFloorplan:Wollen Sie den Grundrissplan wirklich löschen?`,
        },
      })
      .onClose$.pipe(switchMap(() => this.deleteFloorplan(floorplan)))
      .subscribe();
  }

  onCardSubmit(form: FormGroup, plan: Floorplan) {
    plan.name = form.value.name;
    plan.description = form.value.description;
    plan.tags = form.value.tags ?? [];

    this.projectFloorplanService
      .updateFloorplanInIndexedDB(plan)
      .pipe(switchMap(() => this.loadFloorplans()))
      .subscribe();
  }

  onPlanUploaded(plan: UploadedFloorplan) {
    this.projectFloorplanService
      .saveFloorplanToIndexedDB(this.project, plan.name, plan.fileUrl, plan.description, plan.tags)
      .pipe(switchMap(() => this.loadFloorplans()))
      .subscribe();
  }

  openFloorplan(floorplan: Floorplan) {
    this.router.navigate(["/projects", this.project.id, "floorplans", floorplan.id]);
  }

  private deleteFloorplan(floorplan: Floorplan) {
    return this.projectFloorplanService.removeFloorplan(floorplan, this.project).pipe(switchMap(() => this.loadFloorplans()));
  }

  private loadFloorplans() {
    return this.projectFloorplanService.loadFloorplans(this.project).pipe(
      tap((floorplans) => {
        this.floorplans = floorplans;
      }),
    );
  }
}
